import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import Icon, { type IconName } from './ui/Icon';

// ─── Types ──────────────────────────────────────────────────────────────────
export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: { label: string; onClick: () => void };
  ttl: number;
}

interface ToastContextValue {
  push: (message: string, opts?: { kind?: ToastKind; ttl?: number; action?: Toast['action'] }) => void;
  success: (msg: string, opts?: Omit<Parameters<ToastContextValue['push']>[1] & object, 'kind'>) => void;
  error:   (msg: string, opts?: Omit<Parameters<ToastContextValue['push']>[1] & object, 'kind'>) => void;
  info:    (msg: string, opts?: Omit<Parameters<ToastContextValue['push']>[1] & object, 'kind'>) => void;
  warning: (msg: string, opts?: Omit<Parameters<ToastContextValue['push']>[1] & object, 'kind'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────────────
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push: ToastContextValue['push'] = useCallback((message, opts) => {
    setToasts((prev) => {
      // Dedup: if the topmost toast (or any visible one) has the same
      // text + kind, refresh its TTL instead of stacking another copy.
      // Stops the "wall of identical 429 errors" pile-up reported when
      // a rate limit cascades across many in-flight requests.
      const kind = opts?.kind ?? 'info';
      const dup = prev.find((t) => t.message === message && t.kind === kind);
      if (dup) {
        const id = dup.id;
        if (dup.ttl > 0) window.setTimeout(() => remove(id), dup.ttl);
        return prev;
      }
      const id = ++idRef.current;
      const t: Toast = {
        id,
        kind,
        message,
        action: opts?.action,
        ttl: opts?.ttl ?? 5000,
      };
      if (t.ttl > 0) window.setTimeout(() => remove(id), t.ttl);
      return [...prev, t];
    });
  }, [remove]);

  const api: ToastContextValue = {
    push,
    success: (msg, opts) => push(msg, { ...(opts ?? {}), kind: 'success' }),
    error:   (msg, opts) => push(msg, { ...(opts ?? {}), kind: 'error' }),
    info:    (msg, opts) => push(msg, { ...(opts ?? {}), kind: 'info' }),
    warning: (msg, opts) => push(msg, { ...(opts ?? {}), kind: 'warning' }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

// ─── Viewport (fixed bottom-right stack) ────────────────────────────────────
function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div
      style={{ position: 'fixed', // Moved from bottom:16 to top-right — was hidden behind the global
        // AI (✦) and Texting (💬) floating buttons at bottom-right, which
        // made success toasts look like nothing happened after Create Task.
        top: 70, right: 16, zIndex: 10000, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420, pointerEvents: 'none' }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

const KIND_STYLE: Record<ToastKind, { bg: string; fg: string; border: string; icon: IconName }> = {
  success: { bg: 'var(--good-tint)', fg: 'var(--good-fg)', border: 'var(--good-border)', icon: 'check-circle' as IconName },
  error:   { bg: 'var(--danger-tint)', fg: 'var(--danger-fg)', border: 'var(--danger-border)', icon: 'alert-circle' as IconName },
  info:    { bg: 'var(--info-tint)', fg: 'var(--brand)', border: 'var(--info-border)', icon: 'info' as IconName },
  warning: { bg: 'var(--warn-tint)', fg: 'var(--warn-fg)', border: 'var(--warn-border)', icon: 'alert' as IconName },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => { setEntered(true); }, []);

  const s = KIND_STYLE[toast.kind];
  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        borderRadius: 8,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        fontSize: 13,
        lineHeight: 1.5,
        transform: entered ? 'translateX(0)' : 'translateX(20px)',
        opacity: entered ? 1 : 0,
        transition: 'transform 0.2s ease, opacity 0.2s ease',
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon name={s.icon} size={16} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ wordBreak: 'break-word' }}>{toast.message}</div>
        {toast.action && (
          <button
            onClick={() => {
              toast.action!.onClick();
              onDismiss();
            }}
            style={{ marginTop: 6, background: 'transparent', border: 'none', color: s.fg, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 12 }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onDismiss}
        style={{ background: 'transparent', border: 'none', color: s.fg, opacity: 0.6, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
